import React, {Component} from 'react';
import {Button, FormGroup, Label, NavLink} from 'reactstrap';
import validator from 'validator';
import './Login.css';
import HTML from '../../common/HtmlHelpers';
import AppColors from '../../common/Colors';
import Utils from "../../common/Utils";
import InputGroup from "../../components/InputGroup/InputGroup";
import AppVariables from "../../common/AppVariables";
import PlatformRender from "../../components/PlatformRender/PlatformRender";
import {DevicePlatforms} from "../../common/Constants";
import NavigationHelper from "../../common/NavigationHelper";
import TTLProxy from "../../common/TTLAsyncProxy";
import ProgressBar from 'react-md-progress-bar';
import {UsersEndpoints} from "../../common/ProxyEndpoints";
import Avatar from "react-avatar";
import LogoHeader from '../../components/LogoHeader/LogoHeader';
import PropTypes from 'prop-types';
import DocumentTitle from 'react-document-title';
import _ from 'lodash';
import {css} from 'glamor';

const accountStyle = css({
    cursor: "pointer",
    '&:hover': {
        backgroundColor: '#e8e8e8'
    }
});

const selectedAccountStyle = css({
    cursor: "pointer",
    backgroundColor: "#b9b9b9",
    '&:hover': {
        backgroundColor: 'rgba(185,185,185,0.52)'
    }
});

const DefaultRoutes = {
    admin: '/dashboard',
    fbo: '/dashboard',
    owner: '/dashboard',
    farmer: '/dashboard'
};

const Messages = {
    InvalidAttempt: 'Invalid login attempt. Please try again!',
    ConnectionError: 'Failed contacting server. Please check network connectivity!',
    ServerFailure: "An error occurred. Please try again!"
};

/**
 * Renders an error element
 * @param props
 * @constructor
 */
const ErrorDisplay = (props) =>{
    return props.error ? <div className="input-group"> <p className="error-label">{props.error}</p> </div> : null;
};


/**
 * A simple form input without any form internal validation
 */
class FormInput extends Component{

    constructor(props){
        super(props);
        this.state = {
            name: props.name
        };
    }

    focus(){
        this.input.focus();
    }

    select(){
        this.input.select();
    }

    getValue(){
        return this.input.value;
    }

    render(){
        return (<FormGroup key={this.state.name}>
            <Label for={this.state.name}> {HTML.renderIcon(this.props.icon)} {this.props.title}</Label>
            <input type={this.props.type} placeholder={this.props.hint}  ref={(r) => this.input = r} className={"form-control form-input "  + (this.props.faulted ? "form-input-error" : null) } onChange={this.props.onChange} />
        </FormGroup>);
    }

}

FormInput.propTypes = {
    name: PropTypes.string,
    onChange: PropTypes.func,
    type: PropTypes.string,
    hint: PropTypes.string,
    icon: PropTypes.string,
    title: PropTypes.string,
    faulted: PropTypes.bool
};

class Login extends Component {

    constructor(props){
        super(props);

        //
        const state = NavigationHelper.getCurrentState();
        this.proxy = TTLProxy.getInstance();

        this.state = {
            isBusy: false,
            faultedInputs: {},
            errorMessage: null,
            loginFormData: null,

            //  Indicates the user must confirm the account he wishes to login
            mustConfirmAccount: false,

            //  The associated accounts for the user
            associatedAccounts: null,

            //  The selected associated account
            selectedAccount: null,

            // capture tip from navigation state
            tip: state ? state.tip : null
        };

        this.onLogin = this.onLogin.bind(this);

        //  keep background color
        this._bodyBgColor = window.document.body.style.backgroundColor;

        //  holds references of inputs
        this._inputs = {};

        //
        this._isMounted = false;
    }

    componentWillMount(){

        this._isMounted = true;

        //  clear existing session
        window.sessionStorage.clear();
    }

    setStateSafe(state){

        if(this._isMounted){
            this.setState(state);
        }

    }

    componentWillUnmount(){
        this._isMounted = false;
    }

    getFormData(){

        return {
            username : this._inputs['username'].getValue(),
            password:this._inputs['password'].getValue()
        };

    }

    faultedInput(source , name , error , input ){
        source[name] = error;
    }

    onLoginPassed(){

        //  navigate to dashboard
        const history = this.props.history;

        //  determine best page to navigate based on user's role here
        //  for now it's the dashboard

        //  navigate to default path
        window.location.href = DefaultRoutes[this.proxy.getUserRole()];
    }

    validateInput(){

        let faults = {  };
        const formData = this.getFormData();
        if(validator.isEmpty(formData.username)){
            faults['username'] = 'Enter your username';
        }

        if (validator.isEmpty(formData.password)) {
            faults['password'] = 'Enter your password';
        }

        this.setStateSafe({ faultedInputs: faults });

        //
        const faultedCount = Object.keys(faults).length;
        if(faultedCount > 0){
            Utils.flashInput(this._inputs[Object.keys(faults)[0]]);
        }

        return faultedCount === 0;
    }

    processLoginResponse(err,formData){

        const session = window.sessionStorage;
        const history = this.props.history;

        let errMessage;
        if( Utils.isConnectionError(err) ){
            errMessage = Messages.ConnectionError;
        }
        else {

            if(err.response &&  err.response.unauthorized){

                //  get body of response
                const body = err.response.body;

                //  account not activated
                if(!_.isNil(body.error) && !_.isNil(body.error.detail) && !_.isNil(body.error.detail.identity)){

                    //  set current activation info
                    session.setItem('activation_info', JSON.stringify( {
                        identity : body.error.detail.identity,
                        username: formData.username,
                        password: formData.password
                    }));

                    //  navigate to account activation phase
                    return history.push('/activate');
                }
                else {

                    //  Invalid login attempt
                    errMessage = Messages.InvalidAttempt;

                    //  highlight username
                    Utils.flashInput(this._inputs['username']);
                }

            }
            else{
                errMessage = Messages.ServerFailure;
            }

        }

        this.setStateSafe({
            isBusy : false,
            errorMessage: errMessage
        });
    }


    forwardLogin(credentials){

        const me = this;
        return this.proxy.login(credentials).then((response) =>{

            if(response && response.ok) {

                //  Set login successful
                me.onLoginPassed();
            }
            else {

                //  Set login response
                me.setStateSafe({
                    isBusy: false,
                    errorMessage: Messages.InvalidAttempt
                });

            }

        }).catch(err => this.processLoginResponse(err, credentials ));

    }

    onLogin(evt){

        if(evt) {
            evt.preventDefault();
            evt.stopPropagation();
        }

        //  we can't submit with busy state or invalid input
        if(this.state.isBusy || !this.validateInput()){
            return;
        }

        const me = this;
        const formData = this.getFormData();
        const history = this.props.history;

        //  set busy state
        this.setStateSafe({
            isBusy: true,
            errorMessage: null
        });

        //
        return this.proxy.processRequest(UsersEndpoints.testLogin(formData)).then(response => {

            const users = response.getPayload();
            if(users.length === 1){

                //  Just proceed with login
                return me.forwardLogin(formData);

            }
            else {

                //  Allow user to confirm the account to login
                me.setStateSafe({

                    //  User must confirm the account for login
                    mustConfirmAccount: true,

                    //
                    loginFormData: formData,

                    //
                    associatedAccounts: users,

                    //  Not busy
                    isBusy: false
                });

            }

        }).catch(err => {

            let errorMessage;
            if(Utils.isConnectionError(err)){
                errorMessage = Messages.ConnectionError;
            }
            else if(err.response && err.response.forbidden ) {
                errorMessage = Messages.InvalidAttempt;
            }

            //
            me.setStateSafe({
                isBusy: false,
                errorMessage: errorMessage || Messages.ServerFailure
            });

            //
            Utils.flashInput(me._inputs["username"]);

        });


    }

    renderBottomFrame(){
        return  (<div className="text-center" style={{'marginTop' : '10px'}}>
                <div>Haven't registered? Register as</div>
                <div className="mb-1"><NavLink href="#" onClick={Utils.navigateLink.bind(this,'/register/owner')} title="Register Tractor Owner" className="register-link">Tractor Owner</NavLink>, <NavLink onClick={Utils.navigateLink.bind(this,'/register/farmer')} title="Register Farmer" className="register-link" href="#">Farmer</NavLink> or <NavLink onClick={Utils.navigateLink.bind(this,'/register/fbo')} title="Register Farmer Based Organization (FBO)" className="register-link" href="#">FBO</NavLink>.</div>
            <a href="#" onClick={Utils.navigateLink.bind(this,'/forgotpassword')} title="Recover lost password" style={{padding: '2px'}}><b>Forgot password?</b></a>
        </div>);
    }

    onInputChanged(key,evt){

        let faulted = this.state.faultedInputs;
        let error = this.state.errorMessage;
        if(faulted.hasOwnProperty(key)){
            delete faulted[key];
            error = null;
        }

        this.setStateSafe({ errorMessage: error  , faultedInputs: faulted });
    }

    saveInput(name,input){
        this._inputs[name] = input;
    }

    renderAccountSelect(){

        const state = this.state;
        return (<div className="p-4">

            <div className="text-center p-2">
                <h5>Choose account type</h5>
                <p>There are multiple accounts associated with your credentials</p>
            </div>

            <div>
                {state.associatedAccounts ? state.associatedAccounts.map((x,k) => {

                    return (<div key={k} className={`p-2 ${x === this.state.selectedAccount ? selectedAccountStyle : accountStyle}`} onClick={() => {

                        this.setStateSafe({
                            selectedAccount: x
                        });

                    }}>


                        <div style={{ display: "inline" }} className="pull-left">
                            <Avatar name={Utils.formatAvatarName(x.username)}  size={32} />
                        </div>

                        <div className="ml-5">
                            <p style={{ fontSize: "small" }} className="mb-0">{x.username}</p>
                            <p style={{ fontSize: "0.8em"}} className="mb-0"><b>Account</b>: {Utils.formatRole(x.role)}</p>
                        </div>

                    </div>);

                }) : null}
            </div>

            <hr/>


            <Button block color={"green"} disabled={!!!this.state.selectedAccount} onClick={() => {

                const selAccount = this.state.selectedAccount;
                if(!window.confirm(`Proceed with ${selAccount.username}?`)){
                    return;
                }

                const formData =  this.state.loginFormData;
                formData.role = selAccount.role;
                this.forwardLogin(formData);

            }}>Select <i className="icon-arrow-right"></i></Button>

            <div className="text-center mt-2">

                <NavLink href="#" onClick={() => {

                    this.setStateSafe({
                        mustConfirmAccount: false,
                        associatedAccounts: null
                    });

                }}> <i className="icon-arrow-left"></i> <b>Use different credentials</b></NavLink>

            </div>
        </div>);

    }

    render() {

        const state= this.state;
        return (<DocumentTitle title={`${AppVariables.AppCompanyName} - Login`}>
            <React.Fragment>
                <PlatformRender platform={DevicePlatforms.Desktop}>
                    <div className="app flex-row align-items-center" style={{backgroundColor: AppColors.PageBackgroundColor }}>
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-md-6">
                                    <div className="card-group mb-0">
                                        <div className="card p-4 login-form animated fadeIn" style={{ minWidth: 450 }} >

                                            <LogoHeader/>

                                            {this.state.mustConfirmAccount ? this.renderAccountSelect() : <form className="card-block" onSubmit={this.onLogin}>


                                                <InputGroup
                                                    name="username"
                                                    icon="icon-user"
                                                    type="text"
                                                    errorMessage={ this.state.faultedInputs['username'] }
                                                    placeholder="Enter username, email or phone no. here..."
                                                    ref={(username) => this.saveInput('username',username)}
                                                    onChange={this.onInputChanged.bind(this,'username')}
                                                    disabled={state.isBusy} />

                                                <InputGroup
                                                    name="password"
                                                    icon="icon-lock"
                                                    type="password"
                                                    errorMessage={this.state.faultedInputs['password']}
                                                    placeholder="Enter your password here..."
                                                    ref={(password) => this.saveInput('password',password)}
                                                    onChange={this.onInputChanged.bind(this,'password')}
                                                    disabled={state.isBusy} />


                                                <ErrorDisplay error={state.errorMessage}/>

                                                {this.state.tip && <div className="alert alert-info">
                                                    {this.state.tip}
                                                </div>}

                                                <div className="input-group">
                                                    <Button block color="green" type="submit" disabled={state.isBusy}>Login</Button>
                                                </div>

                                                {this.state.isBusy ? <ProgressBar color="green" show={true} overlay={false}/> : null}
                                                {this.renderBottomFrame()}
                                            </form>}

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </PlatformRender>
                <PlatformRender platform={DevicePlatforms.Mobile} onActivate={() => { window.document.body.style.backgroundColor = AppColors.PageWhiteBgColor }} onDeactivate={() => { window.document.body.style.backgroundColor = this._bodyBgColor }}>

                    <div style={{width: '100%'}}>

                        {this.state.mustConfirmAccount ? this.renderAccountSelect() : <form onSubmit={this.onLogin}>

                            <div className="container">

                                <div className="clearfix" style={{marginTop: 20 }}>

                                    <div className="text-center" style={{ marginLeft:"auto" , marginRight: "auto" , marginBottom: 4 , marginTop: 30 }}>

                                        <div style={{marginBottom: 16}}>
                                            <img src="/img/logo_small.png" width={220}/>
                                        </div>

                                    </div>

                                </div>

                                <div>

                                    <FormInput
                                        name="username"
                                        type="text"
                                        faulted={!!this.state.faultedInputs['username']}
                                        hint="Username, email or phone no. here..."
                                        onChange={this.onInputChanged.bind(this,'username')}
                                        ref={(r) => this.saveInput('username',r)}/>

                                    <FormInput
                                        name="password"
                                        type="password"
                                        onChange={this.onInputChanged.bind(this,'password')}
                                        faulted={!!this.state.faultedInputs['password']}
                                        hint="Enter your password here..."
                                        ref={(r) => this.saveInput('password',r) } />

                                </div>



                                <ErrorDisplay error={state.errorMessage}/>

                                {this.state.tip && <div className="alert alert-info">
                                    {this.state.tip}
                                </div>}

                                <div className="input-group">
                                    <Button block color="green" type="submit" disabled={state.isBusy}>Login</Button>
                                </div>

                                {this.state.isBusy ? <ProgressBar color="green" show={true} overlay={false}/> : null}
                                {this.renderBottomFrame()}
                            </div>
                        </form>}

                    </div>

                </PlatformRender>
            </React.Fragment>
        </DocumentTitle>);
    }
}

export default Login;



// WEBPACK FOOTER //
// ./src/views/Login/Login.js